import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, CloseButton, FormControl, FormSelect, Tab, Tabs } from 'react-bootstrap';
import { useAppSelector, useAppDispatch } from '../../app/hooks';
import AppPage from '../common/AppPage';
import { queryEquipment, setAdmin, setQuery, setStrn, setType } from './equipmentSlice';
import DataTable from 'react-data-table-component';
import { styles, getColumns } from './tableConfig';
import produce from 'immer';
import { show as showEditEquipment } from './editEquipmentSlice';
import { showAdd as showAddEquipment } from './editEquipmentSlice';

import { useSearchParams } from 'react-router-dom';
import EditEquipmentModal from './EditEquipmentModal';
import { Equipment } from '../../models/equipment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowsRotate } from '@fortawesome/free-solid-svg-icons';
//import { LinkContainer } from 'react-router-bootstrap';
import AddEquipmentModal from './AddEquipmentModal';
import { Link } from 'react-router-dom';

function EquipmentPage() {
  const dispatch = useAppDispatch();
  const query = useAppSelector(state => state.equipment.query);
  const type = useAppSelector(state => state.equipment.type);
  //const meta = useAppSelector(state => state.equipment.meta);
  const equipment = useAppSelector(state => state.equipment.equipment);
  const admin = useAppSelector(state => state.equipment.admin);
  const strn = useAppSelector(state => state.equipment.strn);
  const [alertVisible, setAlertVisible] = useState(true);

  const [searchParams] = useSearchParams();
  const adminParam = searchParams.get('admin');
  const idParam = searchParams.get('id');
  
  useEffect(() => {
    if (adminParam) {
      dispatch(setAdmin(true));
    }
  }, [dispatch, adminParam]);

  useEffect(() => {
    (async () =>{
      await dispatch(queryEquipment());
      if (idParam) {
        dispatch(showEditEquipment(parseInt(idParam)));
      }
    })();
  }, [dispatch, idParam]);

  const handleRefresh = () => {
    dispatch(queryEquipment());
  }

  const handleQueryChange = (value: string) => {
    dispatch(setQuery(value));
  }

  const handleTypeChange = (value: string) => {
    dispatch(setType(value));
  };

  /*const handleMetaChange = (value: string) => {
    dispatch(setMeta(value));
  };*/

  const handleAddClick = () => {
    dispatch(showAddEquipment());
  }

  const handleRowClick = (equipment: Equipment) => {
    dispatch(showEditEquipment(equipment.id));
  }

  const handleStrnChange = (value: string) => {
    dispatch(setStrn(parseInt(value)));
  }

  const filteredColumns = useMemo(() => {
    const columns = getColumns(query);
    return produce(columns, draft => {
      switch (type) {
        case 'weapon': {
          const ids = ['name', 'weaponType', 'ob', 'pb', 'weight', 'damage', 'bashRating', 'tags', 'updated'];
          draft.forEach(x => x.omit = !ids.includes(x.id!.toString()));
          break;
        }
        case 'armor': {
          const ids = ['name', 'slot', 'db', 'pb', 'def', 'abs', 'mvs', 'weight', 'tags', 'updated'];
          draft.forEach(x => x.omit = !ids.includes(x.id!.toString()));
          break;
        }
        case 'trinket': {
          const ids = ['name', 'slot', 'db', 'pb', 'def', 'mvs', 'weight', 'tags', 'updated'];
          draft.forEach(x => x.omit = !ids.includes(x.id!.toString()));
          break;
        }
        case 'horse': {
          const ids = ['name', 'slot', 'db', 'pb', 'def', 'abs', 'mvs', 'weight', 'tags', 'updated'];
          draft.forEach(x => x.omit = !ids.includes(x.id!.toString()));
          break;
        }
      }
      return draft;
    });
  }, [type, query]);
  
  const filteredData = useMemo(() => {
    let data = equipment;
    if (type) {
      data = data.filter(x => {
        let recType = x.type || 'trinket';
        if (x.setup?.toLowerCase()?.includes('horse')) {
          recType = 'horse';
        }
        return recType === type;
      });
    }
    if (strn) {
      const value = strn < 17 ? strn : strn + 1;
      data = data.filter(x => x.weight <= value);
    }
    if (query) {
      const lowerQuery = query.toLowerCase();
      const lowerKeywords = lowerQuery.split(/ +/).filter(x => x);
      const mostColumns = filteredColumns.filter(x => !x.omit && x.id !== 'tags');
      const tagsColumn = filteredColumns.find(x => !x.omit && x.id === 'tags');

      data = data.filter((row: any) => {
        if (mostColumns.some(x => x.selector?.(row)?.toString()?.toLowerCase()?.includes(lowerQuery))) {
          return true;
        }

        if (tagsColumn && row.setup) {
          if (lowerKeywords.every(lowerKeyword => tagsColumn.selector?.(row)?.toString()?.toLowerCase()?.includes(lowerKeyword))) {
            return true;
          }
        }

        return false;
      });
    }  
    return data;
  }, [filteredColumns, equipment, type, strn, query]);

  return (
    <AppPage pageClass="equipment-page">
      <div className="equipment">
        <h5>WoTMud Equipment</h5>

        {alertVisible && <Alert variant="info" className="small text-dark" dismissible={true} onClose={() => setAlertVisible(false)}>
          Found an item that's missing or outdated?  Install the <Link className="text-dark" to="/scripts">Mudlet script</Link> to help keep the equipment up to date.
        </Alert>}

        <div className="row g-0 gx-2 gy-0 mb-3">
          <div className="col-12 col-md-6 col-lg-4 mb-2">
            <label className="xsmall mb-0">SEARCH</label>
            <div className="position-relative">
              <FormControl
                value={query}
                onChange={e => handleQueryChange(e.target.value)}
                size="sm" style={{paddingRight: '1.75rem'}}/>
              {query &&
                <CloseButton style={{position: 'absolute', right: '.375rem', top: '7px', fontSize: '.75rem', lineHeight: 1}} onClick={() => dispatch(setQuery(''))}/>
              }
            </div>
          </div>
          <div className="col-auto mb-2">
            <label className="xsmall mb-0">STR</label>
            <FormSelect className="w-auto" size="sm" aria-label="Strength" value={strn} onChange={e => handleStrnChange(e.target.value)}>
              <option value={9}>9</option>
              <option value={10}>10</option>
              <option value={11}>11</option>
              <option value={12}>12</option>
              <option value={13}>13</option>
              <option value={14}>14</option>
              <option value={15}>15</option>
              <option value={16}>16</option>
              <option value={17}>17</option>
              <option value={18}>18</option>
              <option value={19}>19</option>
            </FormSelect>
          </div>
          {/*<div className="col-auto mb-2">
            <label className="xsmall mb-0">SHOW</label>
            <FormSelect className="w-auto" size="sm" aria-label="Meta" value={meta} onChange={e => handleMetaChange(e.target.value)}>
              <option value="">All</option>
              <option value="meta">Meta</option>
              <option value="non-meta">Non-meta</option>
            </FormSelect>
          </div>*/}
          <div className="col-auto mb-2">
            <label className="xsmall mb-0">&nbsp;</label>
            <Button size="sm" onClick={() => handleRefresh()}><FontAwesomeIcon icon={faArrowsRotate} /></Button>
          </div>
          {/*<div className="col-auto mb-2">
            <label className="xsmall mb-0">&nbsp;</label>
            <LinkContainer to="/scripts">
              <Button className="d-block" size="sm" variant="primary">Contribute</Button>
            </LinkContainer>
          </div>*/}
          {adminParam ? 
          <div className="col-auto mb-2">
            <label className="xsmall mb-0">&nbsp;</label>
            <Button className="d-block" size="sm" variant="primary" onClick={() => handleAddClick()}>Add New</Button>
          </div> : null}
        </div>
      </div>
      
      <Tabs onSelect={k => handleTypeChange(k!)} style={{fontSize: '14px'}}>
        <Tab eventKey="weapon" title="Weapons"></Tab>
        <Tab eventKey="armor" title="Armor"></Tab>
        <Tab eventKey="trinket" title="Trinkets"></Tab>
        <Tab eventKey="horse" title="Horse"></Tab>

      </Tabs>

      <DataTable
        theme="custom"
        columns={filteredColumns}
        data={filteredData}        
        striped={true}
        customStyles={styles}
        defaultSortFieldId={'name'}
        onRowClicked={e => admin ? handleRowClick(e) : undefined}
      />
      
      <AddEquipmentModal />
      <EditEquipmentModal />
    </AppPage>
  );
}

export default EquipmentPage;